var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useRef, useState, useEffect } from 'react';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import hive from '@bombas/hive';
import InView from '../../../../../Carousel/InView';
import ProductImageZoom from '../../../ProductImageZoom/ProductImageZoom';
import * as Styled from './ImageCarouselSmall.styled';
import { useTrackInteractedSlide } from '../../ResponsiveImageGallery.hooks';
/**
 * Component that renders Responsive Images with pagination dots in a Carousel
 * Logic heavily based off on Carousel.js
 */
var ImageCarouselSmall = function (_a) {
    var _b;
    var images = _a.images;
    var carouselRef = useRef(null);
    var _c = useState(0), currentSlideInView = _c[0], setCurrentSlideInView = _c[1];
    var _d = useState(false), hasSnappingRule = _d[0], setHasSnappingRule = _d[1];
    var _e = useState(false), isCarouselRefLoaded = _e[0], setIsCarouselRefLoaded = _e[1];
    var _f = useState(false), isZoomModalOpen = _f[0], setZoomModalOpen = _f[1];
    var _g = useState(images), zoomedImages = _g[0], setZoomedImages = _g[1];
    var _h = useState(false), isPointer = _h[0], setIsPointer = _h[1];
    var _j = useState(''), direction = _j[0], setDirection = _j[1];
    var handleImageZoom = function (e) {
        // checking for window adds support for SSR, where window is undefined on the server and causes errors in rendering
        if (typeof window !== 'undefined') {
            hive.track('ZOOM_LAUNCH');
        }
        var clickedImageId = e.currentTarget.dataset.id;
        var selectedImage = images.find(function (image) { return image.id.toString() === clickedImageId; });
        // put the selected image at the top
        var restImages = __spreadArray([], images, true);
        restImages.splice(restImages.indexOf(selectedImage), 1);
        setZoomedImages(__spreadArray([selectedImage], restImages, true));
        setZoomModalOpen(!isZoomModalOpen);
    };
    var handleTouchMove = function (e) {
        // note: we aware this would also launch with other 2 finger touches like a tap and we are ok with it
        var isPinched = e.targetTouches.length === 2;
        if (isPinched) {
            handleImageZoom(e);
        }
    };
    var handleInViewChange = function (slide, isInView) {
        if (isInView) {
            setCurrentSlideInView(slide);
        }
    };
    var handleInfiniteLoop = function () {
        if (!carouselRef.current) {
            return;
        }
        setHasSnappingRule(false);
        var scrollWidth = carouselRef.current.scrollWidth;
        carouselRef.current.scrollTo({
            left: direction === 'next' ? 0 : scrollWidth,
            behavior: 'smooth'
        });
    };
    var onSlideChange = function () {
        if (!carouselRef.current) {
            return;
        }
        setIsPointer(false);
        var slides = carouselRef.current.children;
        var offsetWidth = slides[0].offsetWidth;
        var currentScrollPosition = carouselRef.current.scrollLeft;
        var sign = direction === 'next' ? 1 : -1;
        carouselRef.current.scrollTo({
            left: currentScrollPosition + sign * offsetWidth,
            behavior: 'smooth'
        });
    };
    var carouselImages = images.map(function (image, index) { return (React.createElement(Styled.Image, { key: image.id || index, "data-id": image.id || index, background: convertColorToThemeHexCode('grey20'), resolution: { small: 620 }, crop: {
            height: 3040,
            width: 3040
        }, src: image.src, alt: image.alt, role: index !== 0 ? 'presentation' : undefined, onClick: handleImageZoom, onTouchMove: handleTouchMove, isLazy: index !== 0, withProgressiveLoading: index !== 0 })); });
    var imageInteractionTracker = useTrackInteractedSlide({
        current: currentSlideInView,
        images: images
    });
    useEffect(function () {
        var _a;
        if (isCarouselRefLoaded) {
            (_a = carouselRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({ left: 0 });
        }
        setCurrentSlideInView(0);
        imageInteractionTracker.reset();
    }, [images.length, (_b = images[0]) === null || _b === void 0 ? void 0 : _b.id]);
    useEffect(function () { return setIsCarouselRefLoaded(!!carouselRef.current); }, [carouselRef.current]);
    return (React.createElement(Styled.ImageCarouselSmallContainer, null,
        React.createElement(Styled.ImageCarouselSmall, { ref: carouselRef, onMouseEnter: function () {
                setHasSnappingRule(true);
                if (carouselRef.current) {
                    carouselRef.current.focus({
                        preventScroll: true
                    });
                }
            }, onTouchStart: function () { return setHasSnappingRule(true); }, onKeyDown: function (e) {
                setHasSnappingRule(true);
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    setDirection('previous');
                    if (currentSlideInView === 0) {
                        handleInfiniteLoop();
                    }
                    else {
                        onSlideChange();
                    }
                }
                if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    setDirection('next');
                    if (currentSlideInView === images.length - 1) {
                        handleInfiniteLoop();
                    }
                    else {
                        onSlideChange();
                    }
                }
            }, hasSnappingRule: hasSnappingRule, tabIndex: 0 }, carouselImages.map(function (image, index) { return (React.createElement(Styled.Content, { tabIndex: 0, key: index, isPointer: isPointer },
            React.createElement(InView, { parent: carouselRef.current, setElementInView: handleInViewChange, position: index, isLazy: false }, image))); })),
        images.length > 1 && (React.createElement(Styled.CarouselDots, { count: images.length, selectedIndex: currentSlideInView })),
        React.createElement(ProductImageZoom, { "data-testid": "image_zoom_modal", isOpen: isZoomModalOpen, images: zoomedImages, onRequestClose: function () { return setZoomModalOpen(false); } })));
};
export default ImageCarouselSmall;
